// Save logo openings and ends so later videos can start from a brand.

import React, { useCallback, useEffect, useState } from 'react';
import { Bookmark, Check, Loader2, Pencil, Trash2 } from 'lucide-react';
import { applyBrandPlates, brandPlatesOf, type EditorState } from '@rehub/propertycut-timeline';
import type { AgentData } from '../../types';
import type { EditorSelection } from './model';
import {
  deleteBrandKit,
  fetchBrandKits,
  saveBrandKit,
  setDefaultBrandKit,
  type SavedBrandKit,
} from '../../services/supabase';

interface SavedBrandingProps {
  userId: string;
  state: EditorState;
  agent: AgentData;
  onEdit: (recipe: (state: EditorState) => EditorState) => void;
  onAgent: (agent: AgentData) => void;
  onSelect: (selection: EditorSelection) => void;
}

type Notice = { tone: 'ok' | 'error'; text: string } | null;

function kitSummary(kit: SavedBrandKit): string {
  if (kit.opening && kit.ending) return 'Opening and end';
  if (kit.opening) return 'Opening';
  if (kit.ending) return 'End';
  return 'Logo';
}

function sortKits(kits: SavedBrandKit[]): SavedBrandKit[] {
  return [...kits].sort((a, b) => {
    if (a.isDefault !== b.isDefault) return a.isDefault ? -1 : 1;
    return b.updatedAt.localeCompare(a.updatedAt);
  });
}

export default function SavedBranding({
  userId,
  state,
  agent,
  onEdit,
  onAgent,
  onSelect,
}: SavedBrandingProps) {
  const [kits, setKits] = useState<SavedBrandKit[]>([]);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState('');
  const [useForNew, setUseForNew] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState('');
  const [confirmRemoveId, setConfirmRemoveId] = useState<string | null>(null);
  const [notice, setNotice] = useState<Notice>(null);
  const plates = brandPlatesOf(state);
  const canSave = Boolean(plates.opening || plates.ending);

  const load = useCallback(async () => {
    try {
      setKits(sortKits(await fetchBrandKits(userId)));
    } catch (err) {
      console.warn('[propertycut] saved branding load failed:', err);
      setNotice({ tone: 'error', text: 'Could not load your saved branding.' });
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    void load();
  }, [load]);

  const applyKit = (kit: SavedBrandKit) => {
    let openingId = '';
    onEdit((current) => {
      const next = applyBrandPlates(current, { opening: kit.opening, ending: kit.ending });
      openingId = next.clips[0]?.blank ? next.clips[0].id : '';
      return next;
    });
    const color = kit.opening?.background || kit.ending?.background;
    onAgent({
      ...agent,
      logo_url: kit.logoUrl ?? undefined,
      logo_url_2: kit.logoUrl2 ?? undefined,
      ...(color ? { brand_color: color } : {}),
    });
    if (openingId) onSelect({ type: 'clip', id: openingId });
    setNotice({ tone: 'ok', text: `Added ${kit.name} to this video.` });
  };

  const saveNew = async () => {
    if (!canSave || busy) return;
    const label = name.trim() || (kits.length === 0 ? 'My branding' : '');
    if (!label) {
      setNotice({ tone: 'error', text: 'Name this branding so you can tell it apart.' });
      return;
    }
    setBusy('save');
    setNotice(null);
    try {
      const saved = await saveBrandKit(userId, {
        name: label,
        logoUrl: agent.logo_url,
        logoUrl2: agent.logo_url_2,
        opening: plates.opening,
        ending: plates.ending,
        makeDefault: kits.length === 0 || useForNew,
      });
      setKits((current) => sortKits([
        saved,
        ...current
          .filter((item) => item.id !== saved.id)
          .map((item) => ({ ...item, isDefault: saved.isDefault ? false : item.isDefault })),
      ]));
      setName('');
      setUseForNew(false);
      setNotice({
        tone: 'ok',
        text: saved.isDefault
          ? `Saved ${saved.name}. New videos will start with it.`
          : `Saved ${saved.name}.`,
      });
    } catch (err: any) {
      setNotice({ tone: 'error', text: err?.message || 'Could not save this branding.' });
    } finally {
      setBusy(null);
    }
  };

  const updateKit = async (kit: SavedBrandKit) => {
    if (!canSave || busy) return;
    setBusy(`update:${kit.id}`);
    setNotice(null);
    try {
      const saved = await saveBrandKit(userId, {
        id: kit.id,
        name: kit.name,
        logoUrl: agent.logo_url,
        logoUrl2: agent.logo_url_2,
        opening: plates.opening,
        ending: plates.ending,
      });
      setKits((current) => sortKits(current.map((item) => (item.id === saved.id ? saved : item))));
      setNotice({ tone: 'ok', text: `Updated ${saved.name}.` });
    } catch (err: any) {
      setNotice({ tone: 'error', text: err?.message || 'Could not update this branding.' });
    } finally {
      setBusy(null);
    }
  };

  const renameKit = async (kit: SavedBrandKit) => {
    if (busy) return;
    setBusy(`rename:${kit.id}`);
    setNotice(null);
    try {
      const saved = await saveBrandKit(userId, {
        id: kit.id,
        name: renameValue,
        logoUrl: kit.logoUrl,
        logoUrl2: kit.logoUrl2,
        opening: kit.opening,
        ending: kit.ending,
      });
      setKits((current) => sortKits(current.map((item) => (item.id === saved.id ? { ...item, ...saved } : item))));
      setRenamingId(null);
      setNotice({ tone: 'ok', text: `Renamed to ${saved.name}.` });
    } catch (err: any) {
      setNotice({ tone: 'error', text: err?.message || 'Could not rename this branding.' });
    } finally {
      setBusy(null);
    }
  };

  const makeDefault = async (kit: SavedBrandKit) => {
    if (busy || kit.isDefault) return;
    setBusy(`default:${kit.id}`);
    setNotice(null);
    try {
      await setDefaultBrandKit(userId, kit.id);
      setKits((current) => sortKits(current.map((item) => ({ ...item, isDefault: item.id === kit.id }))));
      setNotice({ tone: 'ok', text: `New videos will start with ${kit.name}.` });
    } catch (err: any) {
      setNotice({ tone: 'error', text: err?.message || 'Could not change the default branding.' });
    } finally {
      setBusy(null);
    }
  };

  const removeKit = async (kit: SavedBrandKit) => {
    if (busy) return;
    setBusy(`remove:${kit.id}`);
    setNotice(null);
    try {
      await deleteBrandKit(userId, kit.id);
      setKits((current) => {
        const rest = current.filter((item) => item.id !== kit.id);
        if (!kit.isDefault || rest.length === 0) return rest;
        const newest = [...rest].sort((a, b) => b.updatedAt.localeCompare(a.updatedAt))[0];
        return sortKits(rest.map((item) => ({ ...item, isDefault: item.id === newest.id })));
      });
      setConfirmRemoveId(null);
      setNotice({ tone: 'ok', text: `Removed ${kit.name}.` });
    } catch (err: any) {
      setNotice({ tone: 'error', text: err?.message || 'Could not remove this branding.' });
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="rounded-lg border border-slate-800 bg-slate-900/40 p-3 space-y-3">
      <div>
        <p className="text-xs font-medium text-slate-200 flex items-center gap-1.5">
          <Bookmark className="w-3.5 h-3.5 text-teal-300" />
          Saved branding
        </p>
        <p className="text-[11px] text-slate-500 mt-1">
          Save a logo opening and end for each brand. New videos start with the default.
        </p>
      </div>

      {loading ? (
        <p className="text-[11px] text-slate-500 flex items-center gap-1.5">
          <Loader2 className="w-3 h-3 animate-spin" />
          Loading…
        </p>
      ) : kits.length > 0 && (
        <ul className="space-y-2 max-h-72 overflow-y-auto">
          {kits.map((kit) => {
            const renaming = renamingId === kit.id;
            return (
              <li key={kit.id} className="rounded-md border border-slate-800 bg-slate-950/50 p-2 space-y-2">
                <div className="flex items-center gap-2">
                  {(kit.logoUrl || kit.logoUrl2) && (
                    <div className="h-8 min-w-12 rounded border border-slate-700 bg-slate-950 flex items-center justify-center gap-0.5 overflow-hidden shrink-0 px-1">
                      {kit.logoUrl && <img src={kit.logoUrl} alt="" className="max-h-6 max-w-8 object-contain" />}
                      {kit.logoUrl2 && <img src={kit.logoUrl2} alt="" className="max-h-6 max-w-8 object-contain" />}
                    </div>
                  )}
                  <div className="min-w-0 flex-1">
                    {renaming ? (
                      <input
                        value={renameValue}
                        onChange={(event) => setRenameValue(event.target.value)}
                        onKeyDown={(event) => {
                          if (event.key === 'Enter') void renameKit(kit);
                          if (event.key === 'Escape') setRenamingId(null);
                        }}
                        maxLength={60}
                        autoFocus
                        aria-label="Branding name"
                        className="w-full rounded border border-slate-700 bg-slate-950 px-2 py-1 text-xs text-slate-100"
                      />
                    ) : (
                      <p className="text-xs text-slate-100 truncate">{kit.name}</p>
                    )}
                    <p className="text-[11px] text-slate-500">
                      {kitSummary(kit)}
                      {kit.isDefault ? ' · Default' : ''}
                    </p>
                  </div>
                  <button
                    type="button"
                    disabled={busy !== null}
                    onClick={() => {
                      if (renaming) {
                        void renameKit(kit);
                        return;
                      }
                      setRenamingId(kit.id);
                      setRenameValue(kit.name);
                      setConfirmRemoveId(null);
                    }}
                    className="shrink-0 text-slate-500 hover:text-slate-200 disabled:opacity-40"
                    aria-label={renaming ? 'Save name' : `Rename ${kit.name}`}
                  >
                    {busy === `rename:${kit.id}` ? (
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    ) : renaming ? (
                      <Check className="w-3.5 h-3.5 text-teal-300" />
                    ) : (
                      <Pencil className="w-3.5 h-3.5" />
                    )}
                  </button>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  <button
                    type="button"
                    disabled={busy !== null}
                    onClick={() => applyKit(kit)}
                    className="rounded border border-slate-700 px-2 py-1 text-[11px] text-slate-100 hover:border-teal-400/70 disabled:opacity-40"
                  >
                    Use on this video
                  </button>
                  <button
                    type="button"
                    disabled={!canSave || busy !== null}
                    onClick={() => void updateKit(kit)}
                    title="Replace the saved opening and end with this video"
                    className="rounded border border-slate-700 px-2 py-1 text-[11px] text-slate-300 hover:border-teal-400/70 disabled:opacity-40"
                  >
                    {busy === `update:${kit.id}` ? 'Saving…' : 'Update'}
                  </button>
                  {!kit.isDefault && (
                    <button
                      type="button"
                      disabled={busy !== null}
                      onClick={() => void makeDefault(kit)}
                      className="rounded border border-slate-700 px-2 py-1 text-[11px] text-slate-300 hover:border-teal-400/70 disabled:opacity-40"
                    >
                      {busy === `default:${kit.id}` ? 'Saving…' : 'Use for new videos'}
                    </button>
                  )}
                  {confirmRemoveId === kit.id ? (
                    <button
                      type="button"
                      disabled={busy !== null}
                      onClick={() => void removeKit(kit)}
                      className="rounded border border-red-400/40 px-2 py-1 text-[11px] text-red-300 disabled:opacity-40"
                    >
                      {busy === `remove:${kit.id}` ? 'Removing…' : 'Remove'}
                    </button>
                  ) : (
                    <button
                      type="button"
                      disabled={busy !== null}
                      onClick={() => {
                        setConfirmRemoveId(kit.id);
                        setRenamingId(null);
                      }}
                      className="inline-flex items-center gap-1 px-1.5 py-1 text-[11px] text-slate-500 hover:text-red-300 disabled:opacity-40"
                      aria-label={`Remove ${kit.name}`}
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}

      <form
        className="space-y-2"
        onSubmit={(event) => {
          event.preventDefault();
          void saveNew();
        }}
      >
        <label className="block text-[11px] text-slate-500">
          Brand name
          <input
            value={name}
            onChange={(event) => setName(event.target.value)}
            maxLength={60}
            placeholder={kits.length === 0 ? 'My branding' : 'e.g. City office'}
            className="mt-1 w-full rounded-md border border-slate-700 bg-slate-950 px-2 py-1.5 text-xs text-slate-100 placeholder:text-slate-600"
          />
        </label>
        {kits.length > 0 && (
          <label className="flex items-center gap-2 text-[11px] text-slate-400">
            <input
              type="checkbox"
              checked={useForNew}
              onChange={(event) => setUseForNew(event.target.checked)}
            />
            Use for new videos
          </label>
        )}
        <button
          type="submit"
          disabled={!canSave || busy !== null}
          className="flex w-full items-center justify-center gap-1.5 rounded-md border border-slate-700 px-3 py-2 text-xs text-slate-100 hover:border-teal-400/70 disabled:opacity-40 disabled:hover:border-slate-700"
        >
          {busy === 'save' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5 text-teal-300" />}
          {kits.length === 0 ? 'Save this opening & end' : 'Save as another branding'}
        </button>
        {!canSave && !loading && (
          <p className="text-[11px] text-slate-500">Add an opening or end plate above, then save it.</p>
        )}
      </form>

      {notice && (
        <p className={`text-[11px] ${notice.tone === 'ok' ? 'text-teal-300' : 'text-amber-200'}`}>{notice.text}</p>
      )}
    </div>
  );
}
